/**
 * Created by Jie Gao on 2019-04-16.
 */

require("@nolimitcity/slot-game/lib/gsap/src/umd/CustomBounce.js");

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ReelPartAnimator} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelPartAnimator";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageEvent} from "@nolimitcity/slot-game/bin/core/stage/event/StageEvent";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {AvalancheReelPartAnimator} from "@nolimitcity/slot-game/bin/game/reel/avalanchereel/AvalancheReelPartAnimator";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {DragonTribeGameConfig} from "../DragonTribeGameConfig";
import {DragonTribeSoundConfig} from "../DragonTribeSoundConfig";

export class DTReelPartAnimator extends AvalancheReelPartAnimator {

    private _wasNearWinSymbol:boolean = false;
    private _nearWinBounce:TweenLite;

    public get targetPositions():number[] {
        return this._targetPositions;
    }

    constructor(reelId:number) {
        super(reelId);
        CustomBounce.create("dropBounce", {strength : 0.15, squash : 0, squashID : "drop"});
        CustomBounce.create("shakeBounce", {strength : 0.8, squash : 0, endAtStart : true, squashID : "shake"});
        EventHandler.addEventListener(this, StageEvent.STAGE_RESIZED, (event:GameEvent) => this.onStageResized(event.params[0]));
    }

    private onStageResized(resizeData:IResizeData):void {
        if(this._nearWinBounce && this._nearWinBounce.isActive()) {
            this._nearWinBounce.progress(1);
            if(StageManager.legacyScaling) {
                StageManager.stage.position.set(NolimitApplication.screenBounds.left, NolimitApplication.screenBounds.top);
            }
        }
    }

    protected initTargetPositions():void {
        this._targetPositions = [];
        let targetPosition:number;
        const positionsNumTotal:number = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];
        for(let i:number = 0; i < positionsNumTotal; i++) {
            targetPosition = this.symStep * (i - positionsNumTotal) + this.symHeight / 2;
            this._targetPositions.push(targetPosition);
        }
    }

    public resetSize(symHeight:number, updateSymbols:boolean):void {
        this.symHeight = symHeight;
        this.initTargetPositions();
        if(updateSymbols) {
            this.initSymbolsPosition();
        }
    }

    public resetStartSpin(isFastSpin:boolean):void {
        super.resetStartSpin(isFastSpin);
        this._wasNearWinSymbol = false;
    }

    protected addSymbolInAnimationToTimeline(symbol:StateReelSymbol, timeline:Timeline, delay:number, targetPosition:number, firstSymbol:boolean, nearWin:boolean, triggerAttention:boolean):number {
        if(symbol.symName.indexOf("X") > -1) {
            return delay;
        }

        if(symbol.stackedSymName!.isStacked && !symbol.visible) {
            return delay; // do not play invisible stacked symbols.
        }

        if(symbol.y === targetPosition) {
            return delay;
        }

        const distanceFactor:number = MathHelper.roundToDecimals((targetPosition - symbol.y) / GameConfig.instance.REEL_AREA_HEIGHT, 3);
        delay += ((nearWin || this._wasNearWinSymbol) && !this.isQuickStop) ? AvalancheReelPartAnimator._nearWinStopSymbolDelay[this._reelId] : this.stopSymDelay;
        // Avalanche symbol should not change state.
        if(symbol.currentState!.keyword === DragonTribeGameConfig.instance.SYMBOL_STATES.spin.keyword) {
            const unBlurSymbolTimeline:TimelineLite = new TimelineLite({paused : true});
            unBlurSymbolTimeline.add(symbol.changeState({
                state : GameConfig.instance.REEL_SPIN_STOP_SYMBOL_STATE ? GameConfig.instance.REEL_SPIN_STOP_SYMBOL_STATE() : GameConfig.instance.SYMBOL_STATES.normal,
                replay : false,
                setStack : true,
                fadeInDuration : GameConfig.instance.REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION * distanceFactor,
                fadeInDelay : GameConfig.instance.REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY * distanceFactor
            }));
            timeline.add(() => unBlurSymbolTimeline.play(), delay);
            timeline.add(() => {}, delay + unBlurSymbolTimeline.totalDuration());
        }
        const stopSpeed:number = (nearWin && !this.isQuickStop) ? ReelPartAnimator._nearWinSpinSpeeds[this._reelId] : this.stopSpeed;
        const wasNearWin:boolean = this._wasNearWinSymbol;
        timeline.add(TweenLite.to(symbol, stopSpeed * distanceFactor, { // I don't abstract the time out here because we should override the whole animation function.
            ease : "dropBounce",
            y : targetPosition,
            onComplete : () => {
                if(nearWin && !this.isQuickStop) {
                    SlotGame.sound.playEffect(DragonTribeSoundConfig.ANTICIPATION_SYMBOL_LANDING);
                    this._nearWinBounce = TweenLite.to(StageManager.stage, 0.4, {y : "+=" + 30, ease : "shakeBounce"});
                } else if(firstSymbol || wasNearWin) {
                    SlotGame.sound.playEffect(DragonTribeSoundConfig.REELS_DEFAULT_BOUNCE[Math.floor(Math.random() * 10)]);
                }
                if(triggerAttention) {
                    this.onSymbolInAttentionPointReached(symbol);
                }
            }
        }), delay);

        this._wasNearWinSymbol = nearWin && symbol.symName === ReelSymbolName.SCATTER;

        return delay;
    }

    protected addSymbolOutAnimationToTimeline(symbol:StateReelSymbol, timeline:Timeline, delay:number, onSymbolCompleteCallback:(symbol:StateReelSymbol) => void):number {
        if(symbol.symName.indexOf("X") > -1) {
            onSymbolCompleteCallback(symbol);
            return delay;
        } else {
            return super.addSymbolOutAnimationToTimeline(symbol, timeline, delay, onSymbolCompleteCallback);
        }
    }
}